import { ChangeDetectionStrategy, ChangeDetectorRef, Component, ElementRef, HostListener, Injector, OnDestroy, QueryList, ViewChildren } from '@angular/core';
import { deepClone } from '@cf-shared/deep-helpers';
import { sleep } from '@cf-shared/shared-util';
import { ToastController } from 'providers/toast-controller';
import { TranslatedString } from 'models/translated-string';
import { DestroyGuard } from 'services/browser-utils';
import { SettingsService } from 'services/settings.service';

import { LL } from 'i18n/i18n.service';
export enum ToastType {
  SuccessLight = 'success-light',  // Blue info style (default)
  SuccessStrong = 'success-strong', // Green success style
  WarningLight = 'warning-light',   // Yellow warning style
  WarningStrong = 'warning-strong'  // Red error style
}

/**
 * How a toast offers to close itself. Decided by `getCloseForm()` from options the caller
 * already passes — there is no option to choose a form directly.
 *
 * A toast never carries two of these at once. Before #5116 a toast with an action showed the
 * word "UNDO" with a "✕" stacked directly beneath it: two controls a few points apart on the
 * same edge, meaning opposite things, which is easy to press by mistake on a phone.
 */
export enum ToastCloseForm {
  /** Nothing to press. The `duration` timer is what closes it. */
  None = 'none',
  /** A ✕ floated into the top-right corner of the message. Only when there is no action row. */
  CornerX = 'corner-x',
  /** A worded button sharing the action row with the action itself. */
  WordedButton = 'worded-button',
}

/**
 * Does this toast put a row of buttons under its message? True when the caller gave it
 * something to do beyond closing — an action, or the "don't show again" choice.
 */
export function toastHasActionRow(opts: ToastOptions): boolean {
  return !!opts.actionButtonText || !!opts.showDontShowAgainButton;
}

/**
 * Which of the three closing forms a toast gets. See `ToastCloseForm`.
 *
 * `showCloseButton` left undefined means yes, which is the long-standing default — but only
 * when there is nothing else to close the toast with. A toast whose one button is "Never show"
 * gets no second close control, matching what the template did before #5116, where the close
 * mark was explicitly suppressed whenever that button was present.
 */
export function resolveToastCloseForm(opts: ToastOptions): ToastCloseForm {
  if (opts.showCloseButton === false) {
    return ToastCloseForm.None;
  }
  // if the caller did not ask for a close either way, "don't show again" is already the way out
  if (opts.showCloseButton === undefined && opts.showDontShowAgainButton) {
    return ToastCloseForm.None;
  }
  // with a row of buttons already on screen, the close goes in it as a word, rather than
  // becoming a second control on the same edge as the action.
  return toastHasActionRow(opts) ? ToastCloseForm.WordedButton : ToastCloseForm.CornerX;
}

/**
 * Can the user make this toast go away at all? Every button in the action row dismisses it, so
 * an action or a "don't show again" counts just as much as a close control does.
 */
export function canToastBeDismissedByUser(opts: ToastOptions): boolean {
  return resolveToastCloseForm(opts) !== ToastCloseForm.None || toastHasActionRow(opts);
}

export interface ToastOptions {
  uniqueId?: string; // used to make sure the same type of toast is not shown twice
  message?: TranslatedString;
  message2?: TranslatedString;  // for two-line toasts
  messageLink?: TranslatedString; // Inline link text appended to message (e.g., "View")
  onMessageLink?: () => void;     // Callback when inline link is clicked
  type?: ToastType; // Type of toast for styling (default: SuccessLight)
  cssClass?: string;
  /**
   * How long the toast stays on screen, in milliseconds, before it auto-dismisses.
   *
   * IMPORTANT: there is NO default. If `duration` is omitted (or 0), the toast
   * never auto-dismisses on its own — it stays until the user closes it. So a
   * toast MUST set either a `duration` or `showCloseButton`, otherwise it can
   * get stuck on screen forever. `addToast()` asserts exactly this. See
   * `setToastTimer()` for where the timer is (or is not) armed.
   */
  duration?: number;
  showCloseButton?: boolean;
  closeButtonText?: TranslatedString;
  actionButtonText?: TranslatedString;
  onClose?: () => void;
  onAction?: () => void;
  /**
   * Where the toast appears. With no value the app decides: TOP on a phone, BOTTOM on a wider
   * screen. Pass one to override that for a single toast.
   *
   * The two toasts that carry an Undo override it: the food-added confirmation (#5116) and the
   * Auto-Tune result (#4971). An Undo is worth reaching for straight away, and at the top of a
   * phone it is furthest from the thumb.
   */
  position?: 'top' | 'bottom';
  showDontShowAgainButton?: boolean; // Show "Don't show again" button to permanently dismiss this toast

  // we don't support this because ionic page change detection is unreliable and causes toasts created during page entering to be dismissed
  //dismissOnPageChange?: boolean;
}

export interface Toast {
  id: string; // unique identifier for linking in ViewChildren
  opts: ToastOptions;
  visible?: boolean;
  timer?: ReturnType<typeof setTimeout>;
  pageUrl?: string; // used to track those that need to be dismissed on page change
}

@Component({
    selector: 'toast-host',
    templateUrl: 'toast-host.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class ToastHostComponent implements OnDestroy {
  private destroyGuard = new DestroyGuard();
  protected LL = LL;
  // the template compares against these, and a template cannot reach an enum on its own
  protected readonly ToastCloseForm = ToastCloseForm;
  nextId = 0;
  toasts: Toast[] = [];
  @ViewChildren('toasts') toastRefs: QueryList<ElementRef>;

  constructor(private toastCtrl: ToastController, private cd: ChangeDetectorRef, private injector: Injector) {
    //   app.viewDidEnter.subscribe(() => this.onPageChanged());
  }

  ngOnDestroy() {
    this.destroyGuard.destroy();
  }

  // adjust positions of other toasts when a new one is added or removed
  adjustToastPositions() {
    let bottomOffset = 0;
    let topOffset = 0;
    const isSmallScreen = document.querySelector('cs-app.small-screen') !== null;

    for (let i = this.toasts.length - 1; i >= 0; i--) {
      let toast = this.toasts[i];
      let ref = this.toastRefs.find(ref => ref.nativeElement.id === toast.id);
      if (!ref) {
        continue;
      };
      let elem: HTMLElement = ref.nativeElement;

      // Responsive positioning: mobile = top, desktop = bottom (unless explicitly set)
      const explicitPosition = toast.opts.position;
      const effectivePosition = explicitPosition || (isSmallScreen ? 'top' : 'bottom');

      // Update CSS class based on effective position
      elem.classList.remove('toast-top', 'toast-bottom');
      elem.classList.add(`toast-${effectivePosition}`);

      if (effectivePosition === 'top') {
        // For top-positioned toasts, stack downward from top
        let elemOffset: number;
        if (toast.visible) {
          elemOffset = topOffset;
          topOffset += elem.getBoundingClientRect().height + 15;
        } else {
          elemOffset = topOffset - elem.getBoundingClientRect().height - 100; // flying up
        }

        if (isSmallScreen) {
          elem.style.transform = `translateY(${elemOffset}px)`;
        } else {
          elem.style.transform = `translateX(-50%) translateY(${elemOffset}px)`;
        }
      } else {
        // For bottom-positioned toasts, stack upward from bottom
        let elemOffset: number;
        if (toast.visible) {
          elemOffset = bottomOffset;
          bottomOffset += elem.getBoundingClientRect().height + 15;
        } else {
          // flying down off-screen (negative elemOffset becomes positive after negation)
          elemOffset = -(elem.getBoundingClientRect().height + 100);
        }

        // Bottom toasts need negative offset to go up
        if (isSmallScreen) {
          elem.style.transform = `translateY(${-elemOffset}px)`;
        } else {
          elem.style.transform = `translateX(-50%) translateY(${-elemOffset}px)`;
        }
      }
    }
  }

  ngAfterViewInit() {
    // watch for the moment when some toast is actually added to the DOM
    this.toastRefs.changes.subscribe(() => {
      // position all toasts (new ones start off-screen)
      this.adjustToastPositions();

      // animate in any new toasts (visible=false and no timer means just added)
      for (const toast of this.toasts) {
        if (!toast.visible && !toast.timer) {
          const ref = this.toastRefs.find(r => r.nativeElement.id === toast.id);
          if (ref) {
            // disable transition while setting initial off-screen position
            ref.nativeElement.style.transition = 'none';
          }

          // double rAF: first paints off-screen position, second re-enables transition and animates
          // NOTE: double rAF is sufficient - first frame paints position, second frame re-enables transitions
          requestAnimationFrame(() => {
            requestAnimationFrame(() => {
              if (this.destroyGuard.isDestroyed) return;
              if (ref) {
                // re-enable transition
                ref.nativeElement.style.transition = '';
              }
              toast.visible = true;
              this.adjustToastPositions();
              this.setToastTimer(toast);
              this.cd.markForCheck();
            });
          });
        }
      }
    });
    this.toastCtrl.registerHost(this);
  }

  // reapply the layout when the window is resized
  @HostListener('window:resize', ['$event'])
  onResize(event: Event) {
    void (event);
    this.adjustToastPositions();
  }

  getToastClasses(toast: Toast) {
    let classes = { visible: toast.visible };
    if (toast.opts.cssClass) {
      classes[toast.opts.cssClass] = true;
    }
    // Position class (toast-top/toast-bottom) is now managed dynamically by adjustToastPositions
    // Add type class (default to success-light if not specified)
    const type = toast.opts.type || ToastType.SuccessLight;
    classes[`toast-${type}`] = true;
    return classes;
  }

  getToastIcon(toast: Toast): string | null {
    const type = toast.opts.type || ToastType.SuccessLight;
    switch (type) {
      case ToastType.SuccessLight:
        return 'information-circle';
      case ToastType.SuccessStrong:
        return 'checkmark-circle';
      case ToastType.WarningLight:
        return 'warning';
      case ToastType.WarningStrong:
        return 'warning';
      default:
        return null;
    }
  }

  /** Does this toast put a row of buttons under its message? */
  hasActionRow(toast: Toast): boolean {
    return toastHasActionRow(toast.opts);
  }

  /** Which of the three closing forms this toast gets. See `ToastCloseForm`. */
  getCloseForm(toast: Toast): ToastCloseForm {
    return resolveToastCloseForm(toast.opts);
  }

  /**
   * Is the worded close the FILLED button of the action row? Only when there is no action of
   * its own to fill — the row's last button is always the emphasised one.
   */
  isCloseThePrimaryButton(toast: Toast): boolean {
    return !toast.opts.actionButtonText;
  }

  // onPageChanged() {
  //   for (let t of this.toasts) {
  //     if (t.opts.dismissOnPageChange) {
  //       this.dismissToast(t);
  //     }
  //   }
  // }

  addToast(opts: ToastOptions): Toast {
    // a toast with no timer AND no way to close it stays on screen forever. `showCloseButton`
    // undefined still yields a close control, which the old form of this assert did not know,
    // so it fired on toasts that were in fact fine.
    console.assert(
      opts.duration > 0 || canToastBeDismissedByUser(opts),
      'toast has neither a duration nor a way to close it, so it can never leave:', opts.message);

    // if the toast uses redundancy prevention
    if (opts.uniqueId) {
      // if there is already a toast with the same uniqueId
      let existing = this.toasts.find(t => t.opts.uniqueId === opts.uniqueId);
      if (existing) {
        // just update its options (so that callbacks etc point to the new ones)
        existing.opts = opts;
        // if it has a timer, reset it
        if (existing.timer) {
          clearTimeout(existing.timer);
          this.setToastTimer(existing);
        }
        return existing;
      }
    }


    let toast: Toast = {
      id: 'toast-' + (this.nextId++).toString(),
      opts: deepClone(opts),
      pageUrl: document.location.href,
      visible: false // start hidden for entry animation
    };

    this.toasts.push(toast);
    this.cd.markForCheck();
    // animation is triggered by toastRefs.changes when element is in DOM

    return toast;
  }

  private setToastTimer(toast: Toast) {
    // only arm an auto-dismiss timer when a duration was given. with no duration
    // the toast has no timer at all and stays until the user dismisses it — which
    // is why a toast with neither a duration nor a close button gets stuck (see
    // the assert in addToast() and the note on ToastOptions.duration).
    if (toast.opts.duration) {
      toast.timer = setTimeout(() => {
        this.dismissToast(toast);
      }, toast.opts.duration);
    }
  }

  onClickClose(toast: Toast) {
    if (toast.opts.onClose) {
      toast.opts.onClose();
    }
    this.dismissToast(toast);
  }

  onClickAction(toast: Toast) {
    if (toast.opts.onAction) {
      toast.opts.onAction();
    }
    this.dismissToast(toast);
  }

  onClickMessageLink(toast: Toast) {
    if (toast.opts.onMessageLink) {
      toast.opts.onMessageLink();
    }
    this.dismissToast(toast);
  }

  onClickDontShowAgain(toast: Toast) {
    // if toast has uniqueId, permanently dismiss it
    if (toast.opts.uniqueId) {
      const settings = this.injector.get(SettingsService);
      void settings.markToastDismissedAsync(toast.opts.uniqueId);
    }
    this.dismissToast(toast);
  }

  dismissToast(toast: Toast) {
    void this.asyncDismissToast(toast);
  }
  async asyncDismissToast(toast: Toast) {
    // clear eventual timer if still appearing
    clearTimeout(toast.timer);

    // start animating the disappearance
    toast.visible = false;
    this.adjustToastPositions();
    this.cd.markForCheck();
    // after animation is done (matches $transition-slide-in-out)
    await sleep(350);

    if (this.destroyGuard.isDestroyed) return;
    // physically remove it from the list
    this.toasts = this.toasts.filter(t => t !== toast);
    this.cd.markForCheck();
  }
}
